import React from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Collapse, Tabs, TabsProps } from "antd";
import { FileProtectOutlined, PicRightOutlined } from "@ant-design/icons";
import { Error } from "../../../stories/components/errors/Error";
import LoadingStar from "shared/components/LoadingStar";
import Breadcrumbs from "../../../stories/bread-crumb/Breadcrumbs";
import BackTopButton from "shared/components/BackTopButton";
import { useMercuryContext } from "../../../context";
import InvoiceTypeGeneralInformation from "./InvoiceTypeGeneralInformation";
import ManageRulesTab from "./ManageRulesTab";
import ManageTypeLineItems from "./ManageTypeLineItems";
import { checkPermissions } from "common/utils/checkPermissions";

interface InvoiceTypeDetailsProps {
  context: any;
  location: Location;
  invoiceTypeId: string;
}

interface InvoiceTypeDetailsState {
  error?: string;
  loading: boolean;
  details?: any;
}

class InvoiceTypeDetails extends React.Component<
  InvoiceTypeDetailsProps,
  InvoiceTypeDetailsState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      error: undefined,
      loading: false,
      details: undefined,
    };
  }

  componentDidMount(): void {
    this.fetch();
  }

  async fetch(updating?: boolean, setUpdating?: any) {
    if (!updating) {
      this.setState({ loading: true });
    }
    try {
      const { data } = await this.props.context.invoicingClient.get(
        `/invoicing-v1/types/${this.props.invoiceTypeId}`
      );
      this.setState({ details: data, loading: false });
      if (updating) {
        setUpdating(false);
      }
    } catch (e: any) {
      this.setState({ error: e, loading: false });
    }
  }

  reload = async () => {};

  invoiceTypeDetails() {
    const items = [
      {
        key: "1",
        label: "General Information",
        children: (
          <InvoiceTypeGeneralInformation details={this.state.details} />
        ),
        className: "general-information",
      },
    ];

    return (
      <Collapse
        defaultActiveKey={["1"]}
        items={items}
        style={{ marginBottom: "10px" }}
      />
    );
  }

  tabs() {
    const { details } = this.state;
    
    // @ts-ignore
    const userPermissions = this.props.context?.userDetails?.permissions || [];
    const canViewInvoiceTypes = 
        checkPermissions(userPermissions, [
      {
        customerApplication: "Mercury",
        permissionName: "Invoice Types",
        action: "read",
      },
    ]);

    const tabItems: TabsProps["items"] = [
      ...(canViewInvoiceTypes
        ? [
            {
              key: "1",
              label: (
                <span>
                  <PicRightOutlined /> Line Item Definitions
                </span>
              ),
              children: (
                <ManageTypeLineItems typeId={this.state.details?._id} />
              ),
            },
            {
              key: "2",
              label: (
                <span>
                  <FileProtectOutlined /> Manage Rules
                </span>
              ),
              children: <ManageRulesTab typeId={details._id} typeActive={details.active !== false && !details.deleted} />,
            },
          ]
        : []),
    ];

    return <Tabs defaultActiveKey="1" size="small" items={tabItems} />;
  }

  render() {
    const { details } = this.state;
    const { invoiceTypeId } = this.props;

    if (this.state.error) {
      return (
        <Error
          reload={this.reload}
          loading={this.state.loading}
          error={JSON.stringify(this.state.error)}
        />
      );
    }

    if (this.state.loading || !this.state.details) {
      return (
        <LoadingStar
          logo={true}
          title={
            <div>
              Retrieving details for invoice type: <br /> {invoiceTypeId}
            </div>
          }
        />
      );
    }

    return (
      <>
        <Helmet>
          <title>{details?.name || invoiceTypeId} - Mercury © RWS</title>
        </Helmet>
        <div className="project-details-content">
          <Breadcrumbs
            separator="-"
            items={[
              {
                key: "invoiceTypeId",
                label: invoiceTypeId?.slice(-8),
                tooltip: `Invoice Type ID: ${invoiceTypeId}`,
                color: "var(--primary-color)",
              },
              {
                key: "plpId",
                label: details?.name,
              },
            ]}
          />
          <div
            style={{ padding: "5px 0 10px 0" }}
            className="project-details-body"
          >
            {this.invoiceTypeDetails()}
            {this.tabs()}
            <BackTopButton />
          </div>
        </div>
      </>
    );
  }
}

const InvoiceTypeDetailsPage = function (props: any) {
  const context = useMercuryContext();
  const { id } = useParams<{ id: string }>();

  return (
    <InvoiceTypeDetails
      context={context}
      location={props.location}
      invoiceTypeId={id ?? ""}
    />
  );
};

export default InvoiceTypeDetailsPage;
